// ============================================================================
// RecsuitWorkflowPermissionAdapter
// ============================================================================
// Bridges recsuit's RecsuitRoleEngine into the workflow framework's
// IWorkflowPermissionResolver seam. Lives OUTSIDE the recsuit-workflow folder
// because it imports recsuit-domain code (RecsuitRoleEngine via AuthContext).
// The framework folder remains clean.

import React, { useMemo, type ReactNode } from 'react';
import { useAuthContext } from '../contexts/AuthContext';
import {
  WorkflowPermissionProvider,
  type IWorkflowPermissionResolver,
} from '../recsuit-workflow/permissions/WorkflowPermissionContext';

export const RecsuitWorkflowPermissionAdapter: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { recsuitRoleEngine, recsuitRoleCodes } = useAuthContext();

  const resolver: IWorkflowPermissionResolver = useMemo(() => ({
    // Workflow's "permission" string is the recsuit RBAC code in
    // {module}.{action} form. Until recsuit's gate parses that (or the
    // workflow exposes its own permission catalogue), the safest behaviour
    // is to delegate to the role engine: if the engine has loaded and the
    // user has at least one role, accept the call. The server still
    // enforces via [HasPermission].
    hasPermission: (code: string) => {
      if (!recsuitRoleEngine.isLoaded()) return true;
      const idx = code.indexOf('.');
      if (idx > 0) {
        const moduleCode = code.substring(0, idx);
        const actionCode = code.substring(idx + 1);
        return recsuitRoleEngine.hasAction(moduleCode, actionCode);
      }
      return recsuitRoleCodes.length > 0;
    },
    hasRole: (roleCode: string) => recsuitRoleCodes.includes(roleCode),
    roleCodes: () => recsuitRoleCodes,
  }), [recsuitRoleEngine, recsuitRoleCodes]);

  return (
    <WorkflowPermissionProvider resolver={resolver}>
      {children}
    </WorkflowPermissionProvider>
  );
};

export default RecsuitWorkflowPermissionAdapter;
